import api from './index'
import type { ApiResponse, V1ApiResponse } from '@/types'

// 支付相关类型定义（根据后端支付中心接口）
export interface PaymentOrderData {
  // 用户ID
  userId: string | number
  // 支付方式：0-支付宝 等
  paymentType: number
  // 订单类型：0-开通会员 1-购买潮币 等
  orderType: number
  // 商品编码
  itemCode: string
  // 商品ID
  itemId: string | number
  // 货币类型：0-人民币 2-美元（可选）
  currencyType?: number
  // 平台类型：0-PC 1-安卓 2-鸿蒙 3-iOS（可选）
  platformType?: number
  // 预留扩展字段
  [key: string]: any
}

export interface PaymentOrderResponse {
  // 支付订单号（用于后续查询支付结果）
  orderNo?: string
  orderId?: string
  // 金额
  amount?: number
  // 支付二维码或跳转地址
  paymentUrl?: string
  qrCode?: string
  // 支付宝订单信息（URL编码的表单参数字符串）
  alipayOrderInfo?: string
  // 微信支付订单信息
  wechatPayOrderInfo?: string
  // 消息提示
  message?: string
  // 其它字段按需扩展
  [key: string]: any
}

// 支付宝订单查询响应（根据文档结构做宽松定义）
export interface AlipayQueryResult {
  code?: string
  msg?: string
  subCode?: string
  subMsg?: string
  body?: {
    tradeStatus?: string
    [key: string]: any
  }
  [key: string]: any
}

export interface IapProduct {
  id: number
  productCode: string
  productName: string
  productKind: string
  productType: number
  productUnit: number
  productPrice: string
  productDiscount: string
  productPayPrice: string
  rewardCoinAmount: number
  rewardWalletCategoryId: number | null
  rewardWalletCategoryCode: string | null
  rewardExpireType: number
  rewardExpireDays: number
  rewardExpireDateTime: string | null
  showStatus: number
  productDesc: string
  sort: number
  enabled: number
  createUserName: string | null
  createDateTime: string
  updateUserName: string | null
  updateDateTime: string | null
  remark: string | null
}

export interface QueryIapProductsParams {
  pageNo?: number
  pageSize?: number
  productKind?: string
  productType?: number
  showStatus?: number
  productCode?: string
  productName?: string
}

export interface QueryIapProductsData {
  list: IapProduct[]
  total: number
  pageNo: number
  pageSize: number
}

export interface PaymentOrderQuery {
  pageNo?: number
  pageSize?: number
  orderNo?: string
  platformType?: number
  channelOrderNo?: string
  payChannelCode?: string
  orderStatus?: number
  productId?: number
  productCode?: string
  productKind?: string
  productType?: number
  productUnit?: number
}

export interface PaymentOrderItem {
  userId: number
  orderNo: string
  platformType: number
  channelOrderNo: string | null
  orderAmount: string
  payAmount: string
  payChannelId: number
  payChannelCode: string
  payChannelName: string
  orderStatus: number
  orderStatusName: string
  productId: number
  productCode: string
  productName: string
  productKind: string
  productType: number
  productUnit: number
  productPrice: string
  productDiscount: string
  productPayPrice: string
  rewardCoinAmount: number
  rewardWalletCategoryId: number | null
  rewardWalletCategoryCode: string | null
  rewardExpireType: number
  rewardExpireDays: number
  rewardExpireDateTime: string | null
  payTime: string | null
  createDateTime: string
  updateDateTime: string | null
}

export interface PaymentOrdersData {
  list: PaymentOrderItem[]
  total: number
  pageNo: number
  pageSize: number
}

export interface CreateAlipayQrRechargeOrderPayload {
  productCode: string
}

export interface AlipayQrRechargeOrderData {
  orderId: number
  orderNo: string
  platformType: number
  qrCode: string
  payChannelId: number
  payChannelCode: string
  payChannelName: string
  orderStatus: number
  orderAmount: string
  productId: number
  productCode: string
  productName: string
  productKind: string
  rewardAmount: number
  walletCategoryCode: string | null
  createDateTime: string
}

export interface PaymentOrderDetail {
  orderId: number
  orderNo: string
  platformType: number
  channelOrderNo: string | null
  orderStatus: number
  orderStatusName: string
  orderAmount: string
  payAmount: string | null
  payChannelId: number
  payChannelCode: string
  payChannelName: string | null
  productId: number
  productCode: string
  productName: string
  productKind: string
  productType: number
  productUnit: number
  rewardAmount: number
  walletCategoryCode: string | null
  rewardExpireType: number
  rewardExpireDays: number
  rewardExpireDateTime: string | null
  payTime: string | null
  createDateTime: string
  updateDateTime: string | null
}

// 支付API接口
export const paymentApi = {
  /**
   * 创建支付订单（统一下单）
   */
  createPaymentOrder: (data: PaymentOrderData): Promise<ApiResponse<PaymentOrderResponse>> =>
    api.post('/api-order-payment/payment/create', data),

  /**
   * 查询支付宝订单详情
   * @param orderNo 支付订单号
   */
  queryAlipayOrder: (orderNo: string): Promise<ApiResponse<AlipayQueryResult>> =>
    api.get('/api-order-payment/payment/order/query', { params: { orderNo } }),

  /** 创建支付宝订单码充值订单 */
  createAlipayQrRechargeOrder: (
    data: CreateAlipayQrRechargeOrderPayload
  ): Promise<V1ApiResponse<AlipayQrRechargeOrderData>> =>
    api.post('/v1/payments/alipay/qr-recharge-orders', data),

  /** 按商户订单号查询当前用户支付订单 */
  queryOrder: (orderNo: string): Promise<V1ApiResponse<PaymentOrderDetail>> =>
    api.get(`/v1/payments/orders/${encodeURIComponent(orderNo)}`),

  /** 查询内购商品（潮币积分商品） */
  queryIapProducts: (params: QueryIapProductsParams): Promise<V1ApiResponse<QueryIapProductsData>> =>
    api.get('/v1/iap-products', {
      params,
      noAuthRedirect: true,
    } as any),

  /** 分页查询当前用户支付订单 */
  queryOrders: (params: PaymentOrderQuery): Promise<V1ApiResponse<PaymentOrdersData>> =>
    api.get('/v1/payments/orders', { params }),
}

export default paymentApi
